// Cache položek `/specs` per stránka: kód → Promise<položka|null>.
//
// `/specs?codes=` (search API, #135) vrací k produktu parametry pro chipy,
// platnost akční ceny (`action.to`, saleEnd.js) a příznak Bestseller
// (`bestseller: true`, indexer/bestsellers.mjs). Na jedné stránce se na tentýž
// kód ptá víc modulů (detail, sticky lišta, modal košíku, karty), proto se
// odpověď drží tady a na kód jde nejvýš jeden GET za život stránky — dávkově
// po `BATCH`. Od 2026-09-14 jí jdou VŠICHNI konzumenti `/specs` — i spec chipy
// (specChips.js) a běžné ceny velkoobchodu (wholesalePrice.js): dřív měly vlastní
// dotazy s jinou sadou kódů a velkoobchod na výpisu posílal dva GETy.
//
// Nikdy nehází: API mimo / bez konfigurace (Ú0) → `null` = „nekreslit".

import { getIndexConfig } from './searchConfig.js';
import { specs as fetchSpecs } from './searchApi.js';

/** Dávka kódů na jeden `/specs` GET (strop API je 60). */
const BATCH = 48;

const PENDING = new Map();

// Kódy, jejichž položka má nést i `reviews` (searchApi.js `specs`). Detail
// produktu (productDetail.js) sem zapíše svůj kód dřív, než se na něj kdokoli
// zeptá — recenze pak jedou v tomtéž GETu jako chipy, Bestseller a konec akce.
const REVIEWS = new Set();

/** Položka kódu má nést recenze. Volat před prvním `specFor`/`specsFor` toho kódu. */
export function wantReviews(code) {
  if (code) REVIEWS.add(code);
}

/**
 * Položky pro dávku kódů. Vrací `Map<code, položka|null>` — `null` = produkt
 * v indexu není (nebo API mlčí; obojí znamená „nekreslit").
 *
 * `config` = konfigurace dotazu (výchozí `getIndexConfig()`, velkoobchodní ceny
 * `getWholesaleConfig()`). Odpověď na tenant je pro obě stejná, cache je proto
 * společná; bez konfigurace se nic nestahuje ani necachuje — pozdější volající
 * s jinou konfigurací by jinak dostal zapamatované `null`.
 */
export async function specsFor(codes, config) {
  const out = new Map();
  const missing = [];
  const unique = [];
  const cfg = config || getIndexConfig();
  (codes || []).forEach(function (code) {
    if (!code || unique.indexOf(code) >= 0) return;
    unique.push(code);
    if (!PENDING.has(code)) missing.push(code);
  });
  // bez konfigurace se nestahuje: chybějící kódy = null, už rozjeté se dočkají
  if (!cfg) {
    missing.forEach(function (code) {
      out.set(code, null);
    });
    missing.length = 0;
  }

  for (let i = 0; i < missing.length; i += BATCH) {
    const chunk = missing.slice(i, i + BATCH);
    const reviewCodes = chunk.filter(function (code) {
      return REVIEWS.has(code);
    });
    const req = fetchSpecs(chunk, cfg, reviewCodes).then(function (data) {
      const byCode = new Map();
      const items = data && Array.isArray(data.items) ? data.items : [];
      items.forEach(function (item) {
        if (item && item.code) byCode.set(item.code, item);
      });
      return byCode;
    });
    chunk.forEach(function (code) {
      PENDING.set(
        code,
        req.then(function (byCode) {
          return byCode.has(code) ? byCode.get(code) : null;
        }),
      );
    });
  }

  await Promise.all(
    unique.map(function (code) {
      if (!PENDING.has(code)) return null;
      return PENDING.get(code).then(function (item) {
        out.set(code, item);
      });
    }),
  );
  return out;
}

/** Položka jednoho kódu, nebo `null`. Nikdy nehází. */
export async function specFor(code) {
  if (!code) return null;
  const map = await specsFor([code]);
  return map.get(code) || null;
}

/** Jen pro testy: podstrčit známou položku bez sítě. */
export function primeSpecs(code, item) {
  PENDING.set(code, Promise.resolve(item || null));
}
